import first from 'lodash/first'
import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import React, { createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import { useSavedCheckoutLocalData } from '@Domains/Checkout/Hooks/useSavedCheckoutLocalData'
import { CheckoutFlowSpinner } from '@Domains/CheckoutV2/Features/CheckoutSteps/Components/CheckoutFlowSpinner/CheckoutFlowSpinner'
import {
  CheckoutData,
  CheckoutFlowContextProps,
  CheckoutFlowModel,
  CheckoutFlowNavigationStep,
  CheckoutFlowPageStepList,
  CheckoutFlowStep,
  CheckoutFlowSteps,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { useCheckoutFlowNavigation } from '@Domains/CheckoutV2/Features/CheckoutSteps/Hooks/UseCheckoutFlowNavigation'
import { useCheckoutFlowStepValidation } from '@Domains/CheckoutV2/Features/CheckoutSteps/Hooks/UseCheckoutFlowStepValidation'
import { useCheckoutFlowSubmit } from '@Domains/CheckoutV2/Features/CheckoutSteps/Hooks/UseCheckoutFlowSubmit'
import { PhonePrefixCountryCodeMap, PhonePrefixes } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutAddressStep/Utils/PhonePrefixLocaleCodeMap'

const DefaultStepValidation = {
  [CheckoutFlowStep.ADDRESS]: false,
  [CheckoutFlowStep.SHIPPING]: false,
  [CheckoutFlowStep.SUCCESS]: false,
}

const defaultCheckoutFlowModel = {
  billingAddress: {
    streetName: '',
    additionalStreetInfo: '',
    city: '',
    company: '',
    country: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    state: '',
    postalCode: '',
  },
  subscribeToOffers: { checked: false },
  showShippingAddress: { checked: false },
  shippingAddress: {
    streetName: '',
    additionalStreetInfo: '',
    city: '',
    company: '',
    country: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    state: '',
    postalCode: '',
  },
} as CheckoutFlowModel

export const CheckoutFlowContext = createContext<CheckoutFlowContextProps>({
  activeStep: CheckoutFlowSteps.ADDRESS,
  data: {} as CheckoutData,
  failedNavigation: CheckoutFlowSteps.ADDRESS.key,
  failedNavigationAttempts: null,
  isRecording: false,
  model: defaultCheckoutFlowModel,
  setActiveStep: noop,
  setShippingLoading: noop,
  setSubscribeToClub: noop,
  shippingLoading: false,
  startRecording: noop,
  subscribeToClub: false,
  stepValidations: {
    [CheckoutFlowStep.ADDRESS]: false,
    [CheckoutFlowStep.SHIPPING]: false,
    [CheckoutFlowStep.SUCCESS]: false,
  },
  submitCheckout: noop,
  updateStep: noop,
  updateStepValidation: noop,
  zipCodes: {},
})

interface CheckoutFlowProviderProps {
  data: CheckoutData
  loading?: boolean
  zipCodes: Record<string, string>
}

const getDefaultModel = (data: CheckoutData): CheckoutFlowModel => {
  const countries = data.checkoutMeta.addressMetaData?.countries
  const {
    availablePaymentMethods,
    billingAddress,
    shippingAddress,
  } = data.cart

  const phonePrefix = billingAddress?.phonePrefix
    || PhonePrefixCountryCodeMap[first(countries)?.code || '']?.phonePrefix
    || first(PhonePrefixes)!.phonePrefix
  const firstPaymentOption = first(availablePaymentMethods)?.type === 'applepay' && availablePaymentMethods?.length
    ? availablePaymentMethods[1]
    : first(availablePaymentMethods)
  return {
    ...defaultCheckoutFlowModel,
    billingAddress: {
      ...defaultCheckoutFlowModel.billingAddress,
      country: first(countries)?.code || '',
      phonePrefix,
      ...omit(billingAddress, '__typename'),
    },
    shippingAddress: {
      ...defaultCheckoutFlowModel.shippingAddress,
      country: first(countries)?.code || '',
      phonePrefix,
      ...omit(shippingAddress, '__typename'),
    },
    paymentStep: {
      payment: firstPaymentOption,
    },
    subscribeToOffers: { checked: !data.checkoutMeta.isOptInCountry },
    showShippingAddress: { checked: !isEqual(omit(billingAddress, '__typename'), omit(shippingAddress, '__typename')) },
  }
}

export const CheckoutFlowProvider: FC<PropsWithChildren<CheckoutFlowProviderProps>> = ({
  children,
  data,
  loading,
  zipCodes,
}) => {
  const { saveCheckoutLocal } = useSavedCheckoutLocalData()
  const [model, setModel] = useState<CheckoutFlowModel>(getDefaultModel(data))
  const [activeStep, updateActiveStep] = useState<CheckoutFlowNavigationStep>(CheckoutFlowSteps.ADDRESS)
  const [subscribeToClub, setSubscribeToClub] = useState(false)
  const [shippingLoading, setShippingLoading] = useState(false)
  const [stepValidations, setStepValidations] = useState<Record<CheckoutFlowStep, boolean>>(DefaultStepValidation)
  const [isRecording, setIsRecording] = useState(false)
  const {
    validateAddressStep,
    validateDeliveryStep,
    validatePaymentStep,
  }
    = useCheckoutFlowStepValidation(data.checkoutMeta?.addressMetaData || null, stepValidations, data.cart)
  const {
    failedNavigation,
    failedNavigationAttempts,
    hasHadError,
    setActiveStep: trySetActiveStep,
  }
    = useCheckoutFlowNavigation(activeStep, updateActiveStep, stepValidations, CheckoutFlowPageStepList, {
      [CheckoutFlowStep.ADDRESS]: validateAddressStep(model),
      [CheckoutFlowStep.SHIPPING]: validateDeliveryStep() && validatePaymentStep(model),
      [CheckoutFlowStep.SUCCESS]: false,
    })

  const setActiveStep = useCallback((step: CheckoutFlowNavigationStep) => {
    setStepValidations({
      [CheckoutFlowStep.ADDRESS]: validateAddressStep(model),
      [CheckoutFlowStep.SHIPPING]: validateDeliveryStep() && validatePaymentStep(model),
      [CheckoutFlowStep.SUCCESS]: false,
    })
    trySetActiveStep(step)
  }, [model, trySetActiveStep, validateAddressStep, validateDeliveryStep, validatePaymentStep])
  const updateStepValidation: (step: CheckoutFlowStep, valid: boolean) => void = useCallback((step: CheckoutFlowStep, valid: boolean) => {
    setStepValidations(prev => ({
      ...prev,
      [step]: valid,
    }))
  }, [])
  const updateModel: (partialModel: Partial<CheckoutFlowModel>) => void = useCallback((partialModel: Partial<CheckoutFlowModel>) => {
    setModel(prev => ({ ...prev, ...partialModel }))
  }, [])

  const subscribeToCommercial = !data.checkoutMeta.isOptInCountry && !data.cart.club?.isLoggedIn && !!model.subscribeToOffers.checked

  const {
    loading: loadingSubmit,
    submitCheckout,
  } = useCheckoutFlowSubmit(
    data.cart.id,
    data.cart.checksum,
    model,
    stepValidations,
    setActiveStep,
    hasHadError,
    subscribeToClub,
    subscribeToCommercial,
  )

  const updateStep = useCallback(<T extends keyof CheckoutFlowModel>(
    key: keyof CheckoutFlowModel,
    update: Partial<CheckoutFlowModel[T]>,
    overwriteAll: boolean = false,
  ) => {
    if (overwriteAll) {
      updateModel({ [key]: update })
    } else {
      updateModel({ [key]: { ...merge(model[key], update) } })
    }
  }, [model, updateModel])

  // VALIDATION LOOP
  useEffect(() => {
    setStepValidations({
      [CheckoutFlowStep.ADDRESS]: validateAddressStep(model),
      [CheckoutFlowStep.SHIPPING]: validateDeliveryStep() && validatePaymentStep(model),
      [CheckoutFlowStep.SUCCESS]: false,
    })
  }, [model, data.cart, validateAddressStep, validateDeliveryStep, validatePaymentStep, failedNavigationAttempts])

  useEffect(() => {
    saveCheckoutLocal({ isOptInToNewsletter: !data.checkoutMeta.isOptInCountry ? 'yes' : 'no' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value: CheckoutFlowContextProps = useMemo(() => ({
    activeStep,
    data,
    failedNavigation,
    failedNavigationAttempts,
    isRecording,
    model,
    setActiveStep,
    startRecording: () => setIsRecording(true),
    stepValidations,
    submitCheckout,
    updateStep,
    subscribeToClub,
    setSubscribeToClub,
    shippingLoading,
    setShippingLoading,
    updateStepValidation,
    zipCodes,
  }), [
    activeStep,
    data,
    failedNavigation,
    failedNavigationAttempts,
    isRecording,
    model,
    setActiveStep,
    setIsRecording,
    stepValidations,
    shippingLoading,
    setShippingLoading,
    subscribeToClub,
    setSubscribeToClub,
    submitCheckout,
    updateStep,
    updateStepValidation,
    zipCodes,
  ])

  const isLoading = loadingSubmit || loading

  return (
    <CheckoutFlowContext.Provider value={value}>
      {isLoading && <CheckoutFlowSpinner />}
      {children}
    </CheckoutFlowContext.Provider>
  )
}
